import { cn } from "@/lib/utils";

/** The five stages every tax-resolution client moves through, intake → resolution. */
const STAGES = [
  { label: "Intake", sub: "Engagement signed" },
  { label: "Authorization", sub: "Form 8821 / 2848 pending" },
  { label: "Transcripts", sub: "Pull IRS records" },
  { label: "Analysis", sub: "Tax relief report" },
  { label: "Resolution", sub: "File & close" },
] as const;

export const RESOLUTION_STAGES = STAGES.map((s) => s.label);

interface ResolutionJourneyProps {
  /** 0-based index of the stage the client is currently on. */
  currentStep: number;
  className?: string;
}

/**
 * Horizontal 5-stage progress tracker for a client's resolution path
 * (mockup "Resolution journey"). Completed stages get a green bar, the current
 * stage an orange bar + warning-coloured label, upcoming stages a muted bar.
 * Presentational — the caller derives `currentStep`.
 */
export function ResolutionJourney({ currentStep, className }: ResolutionJourneyProps) {
  const current = Math.max(0, Math.min(currentStep, STAGES.length - 1));
  const activeStage = STAGES[current] ?? STAGES[0];
  return (
    <section
      className={cn(
        "bg-card shadow-card flex flex-col gap-5 rounded-xl border p-6",
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-semibold">Resolution journey</h2>
          <span className="text-muted-foreground text-[13px]">
            Where this client is on the path from intake to resolution.
          </span>
        </div>
        <span className="bg-warning-soft text-warning rounded-full px-2.5 py-1 text-xs font-semibold">
          Step {current + 1} · {activeStage.label}
        </span>
      </div>
      <ol className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 lg:grid-cols-5">
        {STAGES.map((stage, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li
              key={stage.label}
              aria-current={active ? "step" : undefined}
              className="flex flex-col gap-2.5"
            >
              <span
                className={cn(
                  "h-1.5 rounded-full",
                  done ? "bg-success" : active ? "bg-primary" : "bg-border",
                )}
              />
              <span className="flex flex-col gap-0.5 leading-tight">
                <span
                  className={cn(
                    "text-[13px] font-semibold",
                    active ? "text-warning" : done ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {stage.label}
                </span>
                <span className="text-muted-foreground text-xs">{stage.sub}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
